import { atom, computed } from "nanostores"

import { getCachedData, setCachedData } from "./idb-utils"
import { queryClient } from ".."
import { getCandles } from "../api/pool-stats-api"
import type { Pool } from "../api/pools-api"
import type { Bar, IBasicDataFeed, ResolutionString } from "../charting_library/charting_library"
import type { TokenInfo } from "@/api/token-api"

const listeners: Record<string, NodeJS.Timeout> = {}

export const $pendingRequests = atom<number>(0)
export const $chartLoading = computed($pendingRequests, (pendingRequests) => pendingRequests > 0)
export const $forceChartRefresh = atom<number>(0)

async function GetNewCandles(
  poolId: string,
  resolution: ResolutionString,
  useCurrency: boolean,
  reverseChart: boolean,
  lastDay = false,
) {
  $pendingRequests.set($pendingRequests.get() + 1)
  console.log("onNewCandlesRequested", poolId, resolution, lastDay)
  const data: Bar[] = await getCandles(poolId, resolution, useCurrency, reverseChart, lastDay)
  $pendingRequests.set($pendingRequests.get() - 1)
  console.log("onNewCandlesFetched", poolId, resolution, lastDay, data.length)

  // Invalidate query client cache
  queryClient.invalidateQueries({ queryKey: ["Get-Token-Price"] })

  return data
}

export const $firstLoad = atom(true)

export function useDataFeed(
  pool: Pool,
  baseToken?: TokenInfo,
  quoteToken?: TokenInfo,
  useCurrency = false,
  isReversed = false,
): IBasicDataFeed | null {
  if (!baseToken || !quoteToken || !pool) return null

  return {
    onReady(callback) {
      setTimeout(
        () =>
          callback({
            supports_marks: false,
            supports_timescale_marks: false,
            supports_time: true,
          }),
        0,
      )
    },
    searchSymbols: (userInput, _exchange, _symbolType, onResult) => {
      onResult([
        {
          symbol: baseToken.ticker,
          full_name: baseToken.name,
          description: baseToken.id,
          exchange: "Botega",
          ticker: baseToken.ticker,
          type: "crypto",
        },
        {
          symbol: quoteToken.ticker,
          full_name: quoteToken.name,
          description: quoteToken.id,
          exchange: "Botega",
          ticker: quoteToken.ticker,
          type: "crypto",
        },
      ])
    },
    async getBars(symbolInfo, resolution, periodParams, onResult) {
      const reverseChart = isReversed
      const cacheKey = `${pool.id}-${resolution}-${useCurrency}-${reverseChart}-8`

      let cache = await getCachedData(cacheKey)

      const data: Bar[] = cache
        ? cache
        : await GetNewCandles(pool.id, resolution, useCurrency, reverseChart)

      // const data: Bar[] = await GetNewCandles(pool.id, resolution, useCurrency, reverseChart)

      // Save the data in the cache
      await setCachedData(cacheKey, data)

      if (data.length === 0) {
        onResult([], { noData: true })
        return
      }

      // If the last candle is before the requested time frame, return no data
      if (data[data.length - 1].time < periodParams.from * 1000) {
        onResult([], { nextTime: data[data.length - 1].time })
        return
      }

      // Filter the data based on the requested time frame
      const bars = data.filter(
        (bar) => bar.time >= periodParams.from * 1000 && bar.time < periodParams.to * 1000,
      )

      // If there is no mode data return the noData flag
      if (bars.length === 0 && data[0].time > periodParams.from * 1000) {
        onResult([], { noData: true })
        return
      }

      const clone = bars.map((bar) => ({ ...bar }))
      onResult(clone)
      return
    },
    async subscribeBars(symbolInfo, resolution, onTick, listenerGuid) {
      const reverseChart = isReversed

      const initial: Bar[] = await GetNewCandles(pool.id, resolution, useCurrency, reverseChart)

      const cacheKey = `${pool.id}-${resolution}-${useCurrency}-${reverseChart}-8`
      let oldCache = await getCachedData(cacheKey)

      if (oldCache) {
        const latestBar = oldCache[oldCache.length - 1]
        const newData = initial.filter((bar) => bar.time > latestBar.time)
        for (const bar of newData) {
          onTick(bar)
        }
      } else {
        for (const bar of initial) {
          onTick(bar)
        }
      }

      await setCachedData(cacheKey, initial)
      const scheduleNextUpdate = async () => {
        console.log("onCandleChartRefresh")
        try {
          if ($firstLoad.get()) {
            const data: Bar[] = await GetNewCandles(
              pool.id,
              resolution,
              useCurrency,
              reverseChart,
              false,
            )
            const bars = data.filter((bar) => bar.time >= initial[initial.length - 1].time)

            if (bars.length > 0) {
              for (const bar of bars) {
                onTick(bar)
              }
            }

            initial.push(...bars)

            await setCachedData(cacheKey, initial)

            $firstLoad.set(false)
            return
          }
          const data: Bar[] = await GetNewCandles(
            pool.id,
            resolution,
            useCurrency,
            reverseChart,
            true,
          )
          const bars = data.filter((bar) => bar.time >= initial[initial.length - 1].time)

          if (bars.length > 0) {
            for (const bar of bars) {
              onTick(bar)
            }
          }

          initial.push(...bars)

          await setCachedData(cacheKey, initial)
        } catch (error) {
          console.error("Error fetching candles:", error)
        }
      }

      // listeners[listenerGuid] = setTimeout(scheduleNextUpdate, 60_000)
      $forceChartRefresh.listen(async () => {
        await scheduleNextUpdate()
      })
    },
    unsubscribeBars(listener) {
      // clearInterval(listeners[listener])
      $forceChartRefresh.off()
    },
    resolveSymbol(symbolName, onSymbolResolvedCallback) {
      setTimeout(() => {
        const token = symbolName === baseToken.ticker ? baseToken : quoteToken

        onSymbolResolvedCallback({
          name: token.name,
          ticker: token.ticker,
          description: token.ticker,
          type: "crypto",
          session: "24x7",
          timezone: "Etc/UTC",
          minmov: 1,
          pricescale: 100000000,
          has_intraday: true,
          intraday_multipliers: [
            "15" as ResolutionString,
            "30" as ResolutionString,
            "45" as ResolutionString,
            "60" as ResolutionString,
            "120" as ResolutionString,
            "180" as ResolutionString,
            "240" as ResolutionString,
            "1D" as ResolutionString,
            "1W" as ResolutionString,
            "1M" as ResolutionString,
          ],
          // all above 15m resolutions are supported
          supported_resolutions: [
            "15" as ResolutionString,
            "30" as ResolutionString,
            "45" as ResolutionString,
            "60" as ResolutionString,
            "120" as ResolutionString,
            "180" as ResolutionString,
            "240" as ResolutionString,
            "1D" as ResolutionString,
            "1W" as ResolutionString,
            "1M" as ResolutionString,
          ],
          exchange: "Botega",
          listed_exchange: "Botega",
          format: "price",
        })
      }, 0)
    },
  }
}

// clean-up old cache localstorage to free up space
// https://github.com/Autonomous-Finance/dexscreener/issues/141
if (localStorage.getItem("candle-data-cleaned") !== "true") {
  for (let i = localStorage.length - 1; i >= 0; i--) {
    const key = localStorage.key(i) as string
    try {
      const value = JSON.parse(localStorage.getItem(key) as string)
      if (Array.isArray(value)) {
        localStorage.removeItem(key)
        console.log(`Removed array from localStorage: ${key}`)
      }
    } catch { }
  }
  localStorage.setItem("candle-data-cleaned", "true")
}
